import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { Redirect } from "react-router-dom";
import { isPublicPath } from "./shared/constants/publicRoutes";
import { useSelector } from "react-redux";
import "./App.css";
import NotFound from "./pages/notFound/NotFound";
import Nav from "./layouts/nav/Nav";
import Preloader from "./layouts/preloader/Preloader";
import axios from "axios";
import { SERVICEURL, keycloakLogin, keycloakURL } from "./appconfig";
import Version from "./pages/versionNotes/Version";
import { Footer } from "./components";
import { RootState } from "./redux/reducers";
import {
  logoutAllTabs,
  logOut,
  isTokenExpired,
  syncMemberIdFromToken,
} from "./utils/auth";
import { listenTokenExpired, resetTokenExpiredEvent } from "./utils/authEvents";
import { TokenExpiryModal } from "./components/tokenExpiryModal";
import Sidebar from "./components/Sidebar/Sidebar";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import { PERMISSIONS } from "./utils/routeAuth";
import InterviewAssistant from "./pages/interviewAssistant/InterviewAssistant";
import Box from "@mui/material/Box";
import { useWiserTheme } from "./hooks/useWiserTheme";
const ErrorPage = lazy(() => import("./pages/error/error"));
const Login = lazy(() => import("./pages/login_temp/Login_temp"));
const Homepage = lazy(() => import("./pages/homepage/Homepage"));
const PersonnelSearch = lazy(
  () => import("./pages/personnel/personnelSearch/PersonnelSearch"),
);
const CandidateDetail = lazy(
  () => import("./pages/candidateDetail/CandidateDetail"),
);
const CandidateTransfer = lazy(() => import("./pages/candidateTransfer/CandidateTransfer"));
const CvCreate = lazy(() => import("./pages/cvCreate/CvCreate"));
const PositionList = lazy(() => import("./pages/positionList/PositionList"));
const PositionDetailPage = lazy(
  () => import("./pages/positionDetail/PositionDetailPage"),
);
const KvkkStatusList = lazy(() => import("./pages/kvkk/KvkkStatusList"));
const KvkkConsentPage = lazy(() => import("./pages/kvkk/KvkkConsentPage"));
const NewApplications = lazy(
  () => import("./pages/newApplications/NewApplications"),
);
const NegativeCandidatesList = lazy(
  () => import("./pages/negativeCandidatesList/NegativeCandidatesList"),
);
const RejectReasonsList = lazy(
  () => import("./pages/rejectReasonsList/RejectReasonsList"),
);
const HrManagementReport = lazy(
  () => import("./pages/hrManagementReport/HrManagementReport"),
);
const PlacementReport = lazy(
  () => import("./pages/placementReport/PlacementReport"),
);
const WiserKpi = lazy(() => import("./pages/wiserKpi/WiserKpi"));
const UsageStatistics = lazy(
  () => import("./pages/usageStatistics/UsageStatistics"),
);
const TalentSpotTestReport = lazy(
  () => import("./pages/talentSpotTestReport/TalentSpotTestReport"),
);
const CandidatesPersonelPage = lazy(
  () => import("./pages/candidatesPersonel/CandidatesPersonelPage"),
);
const CandidateTrackingBoard = lazy(
  () => import("./pages/candidateTrackingBoard/CandidateTrackingBoard"),
);
const WebApplicationWidget = lazy(
  () => import("./pages/webApplicationWidget/WebApplicationWidget"),
);
const CompaniesCustomers = lazy(
  () => import("./pages/companiesCustomers/CompaniesCustomers"),
);
const UserManagement = lazy(
  () => import("./pages/userManagement/UserManagement"),
);
const ExternalSourceMap = lazy(
  () => import("./pages/externalSourceMap/ExternalSourceMap"),
);
const Configuration = lazy(
  () => import("./pages/configuration/Configuration"),
);

const App: React.FC = () => {
  const sidebarOpen = useSelector((state: RootState) => state.ui.sidebar);
  const { tokens, darkModeActive } = useWiserTheme();

  useEffect(() => {
    logoutAllTabs();
    syncMemberIdFromToken(localStorage.getItem("token"));
  }, []);

  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token"),
  );
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    !!localStorage.getItem("token"),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [tokenExpiryModalOpen, setTokenExpiryModalOpen] = useState(false);
  const logoutTimerRef = useRef<number | null>(null);
  const logoutTriggeredRef = useRef(false);

  const executeLogout = useCallback(() => {
    if (logoutTriggeredRef.current) {
      return;
    }
    logoutTriggeredRef.current = true;
    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    void logOut();
  }, []);

  /** Önce bilgilendirme modal'ı, 2 sn sonra çıkış. */
  const scheduleLogout = useCallback(() => {
    setTokenExpiryModalOpen(true);
    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
    }
    logoutTimerRef.current = window.setTimeout(executeLogout, 2000);
  }, [executeLogout]);

  const handleTokenExpiryModalClose = useCallback(() => {
    setTokenExpiryModalOpen(false);
    executeLogout();
  }, [executeLogout]);

  // Token süresi dolduğunda otomatik logout.
  // Eskiden tek bir setTimeout(logOut, expiryMs) vardı; upstream 357274f bunu 5 saniyelik
  // yoklamaya çevirdi. Yoklama, makine uykuya girip zamanlayıcı geç ateşlendiğinde ve
  // token başka bir sekmede değiştiğinde de doğru çalışıyor.
  useEffect(() => {
    // PUBLIC SAYFADA ZIYARETCININ TOKEN DURUMU ALAKASIZ. Ortada suresi dolacak
    // bir oturum yok; aday bir kullanici degil. Bu muafiyet olmadan, Wiser'in
    // daha once kullanildigi HER tarayicida (localStorage'da bayat bir token
    // kalir) aday "Oturum Süresi Doldu" modal'ini gorup 2 saniye sonra login
    // ekranina atiliyordu. Tarayici testinde yakalandi.
    if (isPublicPath(window.location.pathname)) {
      return;
    }

    if (!token) {
      return;
    }

    if (isTokenExpired(token)) {
      scheduleLogout();
      return;
    }

    const intervalId = window.setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (isTokenExpired(currentToken)) {
        window.clearInterval(intervalId);
        scheduleLogout();
      }
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [scheduleLogout, token]);

  // Interceptor'dan gelen "token süresi doldu" olayı (401 veya süresi dolmuş istek).
  useEffect(() => {
    // Ayni gerekce: public sayfa interceptor'i hic kullanmiyor (duz fetch),
    // ama baska bir sekmeden yayin gelebilir.
    if (isPublicPath(window.location.pathname)) {
      return;
    }

    const unsubscribe = listenTokenExpired(() => {
      scheduleLogout();
    });

    return () => {
      unsubscribe();
    };
  }, [scheduleLogout]);

  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) {
        window.clearTimeout(logoutTimerRef.current);
      }
    };
  }, []);

  let redirectPath = keycloakLogin ? "/error" : "/login";

  if (!token) {
    logoutAllTabs();
  }

  useEffect(() => {
    const currentPath = window.location.pathname;

    // Eğer error sayfasındaysan initAuth tetiklenmesin
    if (currentPath === "/error") {
      console.log("Error sayfasındasın, initAuth tetiklenmeyecek.");
      setIsLoading(false);
      setIsLoggedIn(false);
      setToken(null);
      localStorage.removeItem("token");
      return;
    }

    if (!keycloakLogin) {
      if (token !== null) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
      setIsLoading(false);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (token) {
      syncMemberIdFromToken(token);
      setIsLoggedIn(true);
      setIsLoading(false);
      return;
    }

    if (code) {
      console.log("Keycloak code found:", window.location.origin);
      axios
        .post(`${SERVICEURL}api/Home/LoginWithKeycloak`, {
          code,
          redirectUri: window.location.origin,
        })
        .then((response) => {
          const accessToken = response.data.access_token;
          localStorage.setItem("token", accessToken);
          localStorage.setItem("roles", response.data[".roles"]);
          syncMemberIdFromToken(accessToken);
          setIsLoggedIn(true);
          const requestUrl = localStorage.getItem("requestUrl");
          localStorage.removeItem("requestUrl");
          resetTokenExpiredEvent();
          window.location.href = requestUrl ?? "/";
        })
        .catch((err) => {
          console.error("Login error:", err);
          window.location.href = "/error";
        })
        .finally(() => {
          setIsLoading(false);
        });
      return;
    }
    console.log("Keycloak islooggin:", isLoggedIn);
    // PUBLIC YOLLAR MUAF. Buraya kadar gelen "giris yapmamis" ziyaretci normalde
    // Keycloak'a gonderiliyor; adayin KVKK sayfasi icin bu, gecemeyecegi bir
    // giris ekranina dusmek demek olurdu. Bkz. shared/constants/publicRoutes.
    if (isPublicPath(window.location.pathname)) {
      setIsLoading(false);
      return;
    }
    if (keycloakURL && isLoggedIn !== true) {
      localStorage.setItem("requestUrl", window.location.pathname);
      window.location.replace(keycloakURL);
      return;
    }
  }, [token]);

  // Public yolda Preloader gosterilmez: aday icin bekleyecek bir oturum yok ve
  // sayfa Keycloak'in cevabina bagli degil.
  if (isLoading && keycloakLogin && !isPublicPath(window.location.pathname)) {
    return <Preloader />;
  }

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          ...(darkModeActive && {
            backgroundColor: "transparent",
            transition: "background-color 0.2s ease",
          }),
        }}
      >
        <Router>
        <Switch>
          <Route path="/404" component={NotFound} />
          {/*
            KABUGUN DISINDA, bilerek: aday yuzlu tek sayfa. Icerideki Route'lar
            Sidebar/Nav ve oturum varsayan bir duzen icinde render ediliyor;
            burasi kendi kabugunu tasiyor ve hicbir yetki kontrolune girmiyor.
            Yetki URL'deki tek kullanimlik token'da.
          */}
          <Route exact path="/kvkk/consent">
            {/*
              KENDI Suspense'i var: uygulamanin Suspense siniri kabugun ICINDE,
              bu route ise disinda. Sinir olmadan lazy bir bilesen render
              edilemez.
            */}
            <Suspense fallback={<Box sx={{ minHeight: "100vh" }} />}>
              <KvkkConsentPage />
            </Suspense>
          </Route>
          <Route>
            <>
              {isLoggedIn && <Sidebar />}
              {isLoggedIn && <Nav />}
              <div
                className="wiser-main-content"
                style={{
                  marginLeft: isLoggedIn
                    ? sidebarOpen
                      ? "250px"
                      : "65px"
                    : "0px",
                  transition: darkModeActive
                    ? "margin-left 0.3s ease-in-out, background-color 0.2s ease"
                    : "margin-left 0.3s ease-in-out",
                  paddingTop: isLoggedIn ? "64px" : "0px",
                  minHeight: "calc(100vh - 64px)",
                  ...(darkModeActive && { backgroundColor: "transparent" }),
                }}
              >
                <Suspense
                  fallback={
                    <Box sx={{ minHeight: "calc(100vh - 64px)", p: 3 }}>
                      Yükleniyor…
                    </Box>
                  }
                >
                  <Switch>
                    <Route
                      exact
                      path="/"
                      render={() =>
                        isLoggedIn ? (
                          <Homepage />
                        ) : (
                          <Redirect to={redirectPath} />
                        )
                      }
                    />
                    <Route
                      exact
                      path="/personnel-search"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.CANDIDATE}
                          redirectPath={redirectPath}
                        >
                          <PersonnelSearch />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/candidate-tracking"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.WISER_CANDIDATE_TRACKING}
                          redirectPath={redirectPath}
                        >
                          <CandidateTrackingBoard />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      path="/candidate-detail/:id"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.CANDIDATE}
                          redirectPath={redirectPath}
                        >
                          <CandidateDetail />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/login"
                      component={() =>
                        isLoggedIn == false && !keycloakLogin ? (
                          <Login />
                        ) : (
                          <Redirect to="/" />
                        )
                      }
                    />
                    <Route
                      exact
                      path="/candidate-transfer"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.WISER_CANDIDATE_TRANSFER}
                          redirectPath={redirectPath}
                        >
                          <CandidateTransfer />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      path="/cv-create/:memberId?"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.PERSONNAL_CV}
                          redirectPath={redirectPath}
                        >
                          <CvCreate />
                        </ProtectedRoute>
                      )}
                    />
                    <Route exact path="/error" render={() => <ErrorPage />} />
                    <Route
                      exact
                      path="/version-notes"
                      render={() =>
                        isLoggedIn ? (
                          <Version />
                        ) : (
                          <Redirect to={redirectPath} />
                        )
                      }
                    />
                    <Route
                      exact
                      path="/position-list"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.PROJECT}
                          redirectPath={redirectPath}
                        >
                          <PositionList />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/kvkk-status-list"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_KVKK_STATUS}
                          redirectPath={redirectPath}
                        >
                          <KvkkStatusList />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      path="/position-detail/:id"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.PROJECT}
                          redirectPath={redirectPath}
                        >
                          <PositionDetailPage />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      path="/candidates-personel/:id"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.PROJECT}
                          redirectPath={redirectPath}
                        >
                          <CandidatesPersonelPage />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      path="/interview-assistant/:stateId"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.PERSONEL_MEETING}
                          redirectPath={redirectPath}
                        >
                          <InterviewAssistant />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/new-applications"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.WISER_NEW_APPLICATIONS}
                          redirectPath={redirectPath}
                        >
                          <NewApplications />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/negative-candidates-list"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_NEGATIVE_CANDIDATES}
                          redirectPath={redirectPath}
                        >
                          <NegativeCandidatesList />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/reject-reasons-list"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_REJECT_REASONS}
                          redirectPath={redirectPath}
                        >
                          <RejectReasonsList />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/hr-management-report"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_HR_MANAGEMENT}
                          redirectPath={redirectPath}
                        >
                          <HrManagementReport />
                        </ProtectedRoute>
                      )}
                    />
                    {/* Upstream (b7b461a) bu iki route'u yalnizca isLoggedIn ile
                        koruyor; bizde Dalga 1'den ProtectedRoute + izin var. */}
                    <Route
                      exact
                      path="/placement-report"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_PLACEMENT}
                          redirectPath={redirectPath}
                        >
                          <PlacementReport />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/wiser-kpi"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_WISER_KPI}
                          redirectPath={redirectPath}
                        >
                          <WiserKpi />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/usage-statistics"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_USAGE_STATISTICS}
                          redirectPath={redirectPath}
                        >
                          <UsageStatistics />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/talentspot-test-report"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.REPORT_TALENT_SPOT}
                          redirectPath={redirectPath}
                        >
                          <TalentSpotTestReport />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/web-application-widget"
                      render={() =>
                        isLoggedIn ? (
                          <WebApplicationWidget />
                        ) : (
                          <Redirect to={redirectPath} />
                        )
                      }
                    />
                    <Route
                      exact
                      path="/companies-customers"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.MASTER_DATA_ADMIN}
                          redirectPath={redirectPath}
                        >
                          <CompaniesCustomers />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/external-source-map"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.USER_ADMIN}
                          redirectPath={redirectPath}
                        >
                          <ExternalSourceMap />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/webhook-settings"
                      render={() => <Redirect to="/configuration" />}
                    />
                    <Route
                      exact
                      path="/configuration"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.CONFIGURATION}
                          redirectPath={redirectPath}
                        >
                          <Configuration />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/user-management"
                      render={() => (
                        <ProtectedRoute
                          isLoggedIn={isLoggedIn}
                          role={PERMISSIONS.USER_ADMIN}
                          redirectPath={redirectPath}
                        >
                          <UserManagement />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      exact
                      path="/company-register"
                      render={() =>
                        isLoggedIn ? (
                          <Redirect to="/companies-customers" />
                        ) : (
                          <Redirect to={redirectPath} />
                        )
                      }
                    />
                    <Route
                      exact
                      path="/customer-register"
                      render={() =>
                        isLoggedIn ? (
                          <Redirect to="/companies-customers" />
                        ) : (
                          <Redirect to={redirectPath} />
                        )
                      }
                    />
                    <Redirect to="/404" />
                  </Switch>
                </Suspense>
              </div>
              {isLoggedIn && <Footer />}
            </>
          </Route>
        </Switch>
        </Router>
      </div>
      <TokenExpiryModal
        open={tokenExpiryModalOpen}
        onClose={handleTokenExpiryModalClose}
      />
    </>
  );
};

export default App;
